// Selector de clientes con búsqueda remota (nombre, DPI, IP, email o teléfono).
// Debounce de 350ms sobre el texto, carga inicial de los primeros 50 clientes
// para que haya opciones sin tipear, y resolución por detalle cuando el value
// viene preseleccionado (edición o alta desde el Cliente 360) y no está en la
// lista cargada.
import { useState } from 'react';
import { Select, Typography } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { useT } from '../locales';
import { useCliente, useClientesList } from '../features/clientes/api/useClientes';
import type { ClienteRef } from '@aid/shared';
import { useDebouncedValue } from '../lib/useDebouncedValue';

const { Text } = Typography;

const LIMITE_INICIAL = 50;
const DEBOUNCE_MS = 350;

interface ClienteOption extends DefaultOptionType {
  value: number;
  /** Label del select cerrado: nombre + IP entre paréntesis. */
  label: string;
  /** Línea principal del dropdown: solo el nombre. */
  nombre?: string;
  /** Línea secundaria del dropdown: IP o DPI. */
  secondary?: string;
}

interface SelectAccessibilityProps {
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

interface ClienteSelectProps extends SelectAccessibilityProps {
  value?: number | null;
  /** Preserves a caller-provided label while the detail query resolves. */
  preselectedCliente?: ClienteRef;
  onChange?: (id: number | undefined) => void;
  disabled?: boolean;
  allowClear?: boolean;
  placeholder?: string;
}

interface ClienteOptionSource {
  id: number;
  nombre: string | null;
  identificacion: string | null;
  ip: string | null;
}

function clienteOption(c: ClienteOptionSource): ClienteOption {
  return {
    value: c.id,
    label: `${c.nombre ?? `#${c.id}`}${c.ip ? ` (${c.ip})` : ''}`,
    nombre: c.nombre ?? `#${c.id}`,
    secondary: c.ip ?? c.identificacion ?? undefined,
    title: `${c.nombre ?? `#${c.id}`}${c.ip ? ` (${c.ip})` : ''}`,
  };
}

export function ClienteSelect({
  value,
  preselectedCliente,
  onChange,
  disabled,
  allowClear = true,
  placeholder,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: ClienteSelectProps) {
  const t = useT();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, DEBOUNCE_MS);

  const { data, isLoading } = useClientesList({
    limit: LIMITE_INICIAL,
    offset: 0,
    search: debouncedSearch || undefined,
  });

  const clientes = data?.data ?? [];
  const valueEnLista = value != null && clientes.some((c) => c.id === value);
  const idDetalle = value != null && !valueEnLista ? value : 0;
  const detalleQuery = useCliente(idDetalle);
  const detalle = detalleQuery?.data;

  const options: ClienteOption[] = [...clientes.map(clienteOption)];
  if (value != null && !valueEnLista) {
    options.push(
      detalle
        ? clienteOption(detalle)
        : preselectedCliente?.id === value
          ? clienteOption(preselectedCliente)
          : { value, label: `#${value}`, title: `#${value}` },
    );
  }

  const handleChange = (id?: number) => {
    if (id === undefined) setSearch('');
    onChange?.(id);
  };

  return (
    <Select
      id={id}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      style={{ width: '100%' }}
      showSearch={{ filterOption: false }}
      loading={isLoading || (value != null && !valueEnLista && !detalle)}
      disabled={disabled}
      allowClear={allowClear}
      placeholder={placeholder ?? t('common.buscarCliente')}
      defaultActiveFirstOption={false}
      notFoundContent={t('common.noResults')}
      value={value}
      onChange={handleChange}
      onSearch={setSearch}
      options={options}
      optionRender={(option) => {
        const data = option.data as ClienteOption;
        return (
          <div>
            <div>{data.nombre ?? data.label}</div>
            {data.secondary && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {data.secondary}
              </Text>
            )}
          </div>
        );
      }}
    />
  );
}
